/**
 * Anthropic / LLM API key settings: status, set, clear.
 * Never displays the full key — only a masked hint.
 */
import React, { useState, useCallback } from 'react';
import Key from 'lucide-react/dist/esm/icons/key';
import { Button, ConfirmationDialog, Spinner, TextField, Badge } from '../../../components/ui';

type Tr = (key: string, vars?: Record<string, string | number>) => string;

export interface LlmApiKeyStatus {
  configured: boolean;
  hint: string | null;
  using_platform_default: boolean;
}

export interface LlmApiKeyBlockProps {
  tr: Tr;
  status: LlmApiKeyStatus | null;
  loading?: boolean;
  error?: boolean;
  saving?: boolean;
  clearing?: boolean;
  compact?: boolean;
  /** When true, show "using platform default" messaging for org scope */
  orgScope?: boolean;
  onSave: (apiKey: string) => void | Promise<void>;
  onClear: () => void | Promise<void>;
}

const LlmApiKeyBlock: React.FC<LlmApiKeyBlockProps> = ({
  tr,
  status,
  loading = false,
  error = false,
  saving = false,
  clearing = false,
  compact = false,
  orgScope = false,
  onSave,
  onClear,
}) => {
  const [draft, setDraft] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const handleSave = useCallback(async () => {
    const key = draft.trim();
    if (!key) return;
    await onSave(key);
    setDraft('');
  }, [draft, onSave]);

  const handleConfirmClear = useCallback(async () => {
    setConfirmClear(false);
    await onClear();
  }, [onClear]);

  const hasOrgKey = Boolean(status?.configured && !status.using_platform_default);
  const usingPlatform = Boolean(status?.using_platform_default);
  const titleClass = compact ? 'text-xs font-medium text-content' : 'text-sm font-semibold text-content';
  const helpClass = compact ? 'text-[11px] text-content-muted mb-2' : 'text-[11px] text-content-muted mb-4';

  return (
    <div className={compact ? '' : 'p-5 rounded-2xl bg-surface-primary border border-subtle'}>
      <h3 className={`${titleClass} mb-1 flex items-center gap-2`}>
        <Key size={compact ? 12 : 16} className="text-primary" />
        {tr(orgScope ? 'settingsLlmOrgTitle' : 'settingsLlmPlatformTitle')}
      </h3>
      <p className={helpClass}>
        {tr(orgScope ? 'settingsLlmOrgHelp' : 'settingsLlmPlatformHelp')}
      </p>

      {loading ? (
        <Spinner size="sm" />
      ) : (
        <div className={compact ? 'space-y-2' : 'space-y-4'}>
          {error && (
            <p className="text-xs text-status-danger">{tr('settingsLlmError')}</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {hasOrgKey || (status?.configured && !orgScope) ? (
              <>
                <Badge variant="success">{tr('settingsLlmConfigured')}</Badge>
                {status?.hint && (
                  <code className="text-xs font-mono text-content-secondary">{status.hint}</code>
                )}
              </>
            ) : usingPlatform ? (
              <>
                <Badge variant="info">{tr('settingsLlmUsingPlatform')}</Badge>
                {status?.hint && (
                  <code className="text-xs font-mono text-content-secondary">{status.hint}</code>
                )}
              </>
            ) : (
              <Badge variant="warning">{tr('settingsLlmNotConfigured')}</Badge>
            )}
          </div>

          <TextField
            label={tr('settingsLlmFieldLabel')}
            type="password"
            size={compact ? 'sm' : 'md'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={tr('settingsLlmFieldPlaceholder')}
            autoComplete="off"
          />

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={saving}
              disabled={saving || !draft.trim()}
              onClick={() => void handleSave()}
            >
              {tr(hasOrgKey || (status?.configured && !orgScope) ? 'settingsLlmBtnUpdate' : 'settingsLlmBtnSave')}
            </Button>
            {(hasOrgKey || (status?.configured && !orgScope)) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={clearing}
                disabled={clearing}
                onClick={() => setConfirmClear(true)}
              >
                {tr(orgScope ? 'settingsLlmBtnClearOrg' : 'settingsLlmBtnClear')}
              </Button>
            )}
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={handleConfirmClear}
        title={tr('settingsLlmClearConfirmTitle')}
        message={tr(orgScope ? 'settingsLlmClearOrgConfirmMessage' : 'settingsLlmClearConfirmMessage')}
        confirmText={tr(orgScope ? 'settingsLlmBtnClearOrg' : 'settingsLlmBtnClear')}
        cancelText={tr('settingsTenantsBtnCancel')}
        variant="warning"
      />
    </div>
  );
};

export default React.memo(LlmApiKeyBlock);
